/**
 * Customer Group Editor
 * Ügyfélcsoport létrehozása/szerkesztése
 */

import React, { useState, useEffect } from 'react';
import { CustomerGroup } from '../../types/customerGroup';
import { useCustomerGroups } from '../../hooks/useCustomerGroups';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface CustomerGroupEditorProps {
  group?: CustomerGroup;
  onClose: () => void;
}

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
];

const ICONS = ['👥', '⭐', '🎯', '💼', '🏆', '🎓', '💎', '🚀', '🔥', '⚡'];

const CustomerGroupEditor: React.FC<CustomerGroupEditorProps> = ({ group, onClose }) => {
  const { create, update } = useCustomerGroups();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!group;

  // Form state
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    color: group?.color || COLORS[0],
    icon: group?.icon || ICONS[0],
    tags: group?.tags?.join(', ') || '',
    isDynamic: group?.isDynamic || false,
    customerIds: group?.customerIds || []
  });

  // Mentés
  const handleSave = async () => {
    // Validáció
    if (!formData.name.trim()) {
      setError('A név megadása kötelező');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const groupData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        icon: formData.icon,
        tags: formData.tags
          .split(',')
          .map(t => t.trim())
          .filter(t => t.length > 0),
        isDynamic: formData.isDynamic,
        customerIds: formData.customerIds,
        isActive: true
      };

      if (isEditing) {
        await update(group.id, groupData);
        toast.success('Csoport frissítve');
      } else {
        await create(groupData);
        toast.success('Csoport létrehozva');
      }

      onClose();
    } catch (err: any) {
      setError(err.message || 'Hiba történt a mentés során');
      toast.error(err.message || 'Hiba történt a mentés során');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEditing ? 'Csoport szerkesztése' : 'Új ügyfélcsoport'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Név */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Csoport neve *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="pl. VIP ügyfelek"
              />
            </div>

            {/* Leírás */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leírás
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Rövid leírás a csoportról..."
              />
            </div>

            {/* Ikon és szín */}
            <div className="grid grid-cols-2 gap-4">
              {/* Ikon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ikon
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`p-2 text-2xl rounded-md border-2 transition-colors ${
                        formData.icon === icon
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Szín */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Szín
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-md border-2 transition-all ${
                        formData.color === color
                          ? 'border-gray-900 dark:border-white scale-110'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Címkék */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Címkék
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="vip, aktív, prémium (vesszővel elválasztva)"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Vesszővel elválasztva
              </p>
            </div>

            {/* Dinamikus csoport */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isDynamic"
                checked={formData.isDynamic}
                onChange={(e) => setFormData({ ...formData, isDynamic: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isDynamic" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Dinamikus csoport (automatikusan frissül szűrők alapján)
              </label>
            </div>

            {formData.isDynamic && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  ℹ️ A dinamikus csoportok funkció hamarosan elérhető lesz
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Mégse
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Mentés...' : isEditing ? 'Frissítés' : 'Létrehozás'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerGroupEditor;
